import { withFormik, FormikProps } from 'formik';
import * as Yup from 'yup';
import { apiService } from 'core/services/apiService';
import './style.scss';

import { LoginInput } from '../../../../core/components/styles/LoginInput';
import { PasswordInput } from '../../../../core/components/styles/PasswordInput';
import { useAuth } from 'module/auth/context/AuthContext';
import { useEffect } from 'react';
import useModal from 'core/hooks/useModal';
import CenterRemodal from 'core/components/Modal/CenterModal';
import ResetPasswordModal from './ResetPasswordModal';
import emailLogo from 'assets/new_icons/email_icon.png';
import passwordLogo from 'assets/new_icons/password_icon.png';
import CorporateFareIcon from '@mui/icons-material/CorporateFare';
import { CircularProgress } from '@mui/material';

interface FormValues {
	organizationId: string;
	email: string;
	password: string;
}

interface MyFormProps {
	initialOrganizationId?: string;
	initialEmail?: string;
	initialPassword?: string;
	submitClick: (data: FormValues) => void;
	isLoading?: boolean;
}

const InnerForm = (props: FormikProps<FormValues> & MyFormProps) => {
	const { open: isModalOpen, openModal, closeModal } = useModal();

	const {
		values,
		errors,
		touched,
		handleChange,
		handleBlur,
		handleSubmit,
		isSubmitting,
		setSubmitting,
		setFieldValue,
		isLoading
	} = props;

	useEffect(() => {
		// Load saved organization ID on component mount
		const savedOrgId = localStorage.getItem('BYOD-organizationId');
		if (savedOrgId) {
			setFieldValue('organizationId', savedOrgId.trim().toUpperCase());
		}
	}, [setFieldValue]);

	const auth = useAuth();

	useEffect(() => {
		if (isSubmitting) {
			setSubmitting(false);
		}
	}, [auth.error, isSubmitting, setSubmitting]);

	return (
		<div className="authForm">
			<h4 className="logoHeader">
				<b>Hi, I&#39;m Mabel. Welcome!</b>
			</h4>
			<div className="FormBox">
				{/* <h3>Login</h3> */}
				<form onSubmit={handleSubmit}>
					<div className="input-container">
						<CorporateFareIcon className="organisationLogo"/>
						<LoginInput
							placeholder="Organization ID"
							hasError={touched.organizationId && errors.organizationId}
							errorMsg={errors.organizationId}
							name="organizationId"
							type="text"
							autoComplete="organization"
							onChange={(e) => {
								// Convert to uppercase before setting the form value
								const uppercaseValue = (e.target as HTMLInputElement).value.toUpperCase();
								e.target.value = uppercaseValue;
								handleChange(e);
								// Save trimmed and uppercase org id to localStorage
								localStorage.setItem('BYOD-organizationId', uppercaseValue.trim());
							}}
							onBlur={handleBlur}
							value={values.organizationId}
						/>
					</div>
					<div className="input-container">
						<img src={emailLogo} className="emailLogo" alt="Logo" />
						<LoginInput
							placeholder="Username"
							hasError={touched.email && errors.email}
							errorMsg={errors.email}
							name="email"
							type="text"
							autoComplete="username"
							onChange={handleChange}
							onBlur={handleBlur}
							value={values.email}
						/>
					</div>
					<div className="input-container">
						<img src={passwordLogo} className="passwordLogo" alt="Logo" />
						<PasswordInput
							placeholder="Password"
							hasError={touched.password && errors.password}
							errorMsg={errors.password}
							name="password"
							autoComplete="current-password"
							onChange={handleChange}
							onBlur={handleBlur}
							value={values.password}
						/>
					</div>
					{auth.error ? <p className="InputErrorBox">{auth.error}</p> : null}
					<div className="button-container">
						<div className="forgotPassword">
							<p onClick={openModal}>Forgot your password?</p>
						</div>
						<div className="rememberMe">
							<button
								style={{
									backgroundColor: '#446388',
								}}
								type="submit"
								disabled={
									isLoading ||
									isSubmitting ||
									!!(errors.email && touched.email) ||
									!!(errors.password && touched.password) ||
									!!(errors.organizationId && touched.organizationId)
								}
							>
								{isLoading ? <CircularProgress color="inherit" size={25} /> : 'LOGIN'}
							</button>
						</div>
					</div>
				</form>
			</div>
			<p className="loginFooter">
				By Continuing You Agree To Our{' '}
				<a href="https://byod.ai/acceptable-use-policy/" target="_blank" rel="noreferrer">Terms and Conditions</a>
			</p>
			<span className="contact_us">
				<a href="mailto:support@byod.ai">Contact Us </a> |{' '}
				<a href="https://byod.ai/byod-hosting-data-security/" target="_blank" rel="noreferrer">Security </a> |{' '}
				<a href="https://byod.ai/privacy-policy/" target="_blank" rel="noreferrer">
					Privacy Policy
				</a>{' '}
			</span>

			{isModalOpen ? (
				<CenterRemodal
					title="Reset Password"
					close={closeModal}
					render={() => (
						<ResetPasswordModal 
							closeModal={closeModal} 
							onResetPassword={async (email: string, tenant: string) => {
								try {
									await apiService.put('/users/password-recovery', {
										email,
										tenant
									});
								} catch (error) {
									console.error('Failed to send password reset:', error);
									throw error;
								}
							}} 
						/>
					)}
				/>
			) : null}
		</div>
	);
};

export const AuthForm = withFormik<MyFormProps, FormValues>({
	mapPropsToValues: (props) => ({
		organizationId: props.initialOrganizationId || '',
		email: props.initialEmail || '',
		password: props.initialPassword || '',
	}),

	validationSchema: Yup.object().shape({
		// Trim whitespace and validate non-empty organization id
		organizationId: Yup.string()
			.transform((value) => (typeof value === 'string' ? value.trim() : value))
			.required('Organization Id is required'),
		email: Yup.string().email('Username not valid').required('Username is required'),
		password: Yup.string().required('Password is required'),
	}),

	handleSubmit({ organizationId, email, password }: FormValues, { props }) {
		// Ensure organizationId is trimmed and email is lowercase before submission
		props.submitClick({ 
			organizationId: organizationId.trim(), 
			email: email ? email.toLowerCase().trim() : '', 
			password 
		});
	},
})(InnerForm);
